import InfoIcon from '@mui/icons-material/Info'
import {
  Alert as MuiAlert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField,
  Typography
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { QUERY_PARAMS } from '../../../constants'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AccountsDropdown } from '../../common/ChartOfAccountsDropdown'
import { CustomController } from '../../common/CustomController'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { Alert } from '../../common/Alert'
import { BankReconciliationFiltersProps } from '../../../interfaceTypes/props'
import { formatCurrency } from '../../../utils'

const transactionTypeOptions = [
  { id: 'CREDIT', name: 'Credit' },
  { id: 'DEBIT', name: 'Debit' }
]

const reconcileStatusOptions = [
  { id: 'true', name: 'Cleared' },
  { id: 'false', name: 'Uncleared' }
]

const BankReconciliationFilters = ({
  amountToReconcile,
  creditTotal,
  debitTotal,
  accountReconciledData,
  statementBalance: statementBalanceProp = 0,
  onStatementBalanceChange,
  showMonthChangePrompt,
  sessionMonth,
  sessionAccountName,
  monthChangeBusy,
  onConfirmMonthChange
}: BankReconciliationFiltersProps) => {
  const { lastReconciliation, openingBalance, reconciliation: currentReconciliation } = accountReconciledData || {}
  const [searchParams, setSearchParams] = useSearchParams()
  const getFilterValues = (key: QUERY_PARAMS) => searchParams.get(key) || ''
  const transactionTypeParam = getFilterValues(QUERY_PARAMS.TRANSACTION_TYPE)
  const statementMonthParam = getFilterValues(QUERY_PARAMS.STATEMENT_MONTH)
  const reconcileStatusParam = getFilterValues(QUERY_PARAMS.RECONCILIATION_STATUS)
  const lineItemsParam = getFilterValues(QUERY_PARAMS.RECONCILIATION_LINE_ITEMS)

  const methods = useForm<any>({
    mode: 'all',

    defaultValues: {
      statementBalance: 0,
      beginningBalance: 0,
      clearedBalance: 0,
      lineItems: lineItemsParam !== 'false',
      transactionType: transactionTypeOptions.find(option => option.id === transactionTypeParam) || null,
      reconcileStatus: reconcileStatusOptions.find(option => option.id === (reconcileStatusParam || 'false'))
    }
  })

  const { control, watch, setValue } = methods
  // Read-modify-write against the LIVE url, not this render's snapshot. Several effects here write params in the
  // same tick and the parent writes too (picking a session sets the account + statement month together); react
  // -router hands the functional form the params as of the last render, so basing an update on it would silently
  // revert whatever was written since. BrowserRouter pushes to history synchronously, so window.location.search
  // is always current.
  const updateParams = (mutate: (params: URLSearchParams) => void) => {
    setSearchParams(() => {
      const next = new URLSearchParams(window.location.search)
      mutate(next)
      return next
    })
  }
  const setParams = (key: string, value: string) => updateParams(params => params.set(key, value))
  const deleteParam = (key: string) => updateParams(params => params.delete(key))
  const accountId = getFilterValues(QUERY_PARAMS.ACCOUNT_ID)
  const lineItem = watch('lineItems')
  const statementBalance = watch('statementBalance')
  const beginningBalance = watch('beginningBalance')
  const statementMonth = watch('statementMonth')
  const transactionType = watch('transactionType')
  const reconcileStatus = watch('reconcileStatus')
  const clearedBalance = Number(beginningBalance || 0) + amountToReconcile

  // Cancel the month change: snap the Statement picker back to the session's month. Writing the field also
  // updates the URL (via the statementMonth effect below), which clears the prompt in the parent.
  const handleCancelMonthChange = () => {
    if (sessionMonth) setValue('statementMonth', dayjs(sessionMonth))
  }

  const selectedMonthLabel = dayjs(statementMonth || Date()).format('MMMM YYYY')
  // Moving to an EARLIER month trims the selection to that month end on the server, so warn the user explicitly.
  const willTrimTransactions = !!sessionMonth && dayjs(statementMonth || Date()).isBefore(dayjs(sessionMonth), 'month')

  // Checkbox → URL, for the user's own toggle. Only when it actually differs, so this can't fight the effect
  // below (which pushes the URL back into the field when a resumed session restores its saved view).
  useEffect(() => {
    if (typeof lineItem !== 'boolean') return
    // Absent param means ON, so mounting without one writes nothing; only a real difference is pushed.
    if (lineItem !== (lineItemsParam !== 'false')) {
      setParams(QUERY_PARAMS.RECONCILIATION_LINE_ITEMS, lineItem.toString())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItem])

  // URL → checkbox. The view mode is persisted on the reconciliation session and re-applied to the URL when a
  // session is resumed, so the checkbox has to FOLLOW the param rather than keep the copy it mounted with —
  // otherwise it can show "Line Item Detail" ticked while the day-grouped listing is on screen.
  useEffect(() => {
    const next = lineItemsParam !== 'false'
    if (lineItem !== next) setValue('lineItems', next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItemsParam])

  // URL → picker. The statement month lives in the URL and is set from several places (the sessions menu, and
  // hydrating a session switches the view to that session's month), so the picker has to FOLLOW it instead of
  // keeping its own copy — otherwise it keeps showing the previously picked month after a session switch.
  useEffect(() => {
    const target = statementMonthParam ? dayjs(statementMonthParam) : dayjs()
    if (!statementMonth || !dayjs(statementMonth).isSame(target, 'month')) {
      setValue('statementMonth', target)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementMonthParam])

  // Picker → URL, for the user's own edits. Skipped while the field is still empty: on the first render that
  // would overwrite an incoming ?statementMonth (a session's month, a refresh, a bookmark) with today's month.
  useEffect(() => {
    if (!statementMonth) return
    const next = dayjs(statementMonth).format('YYYY-MM-DD')
    if (next !== statementMonthParam) {
      setParams(QUERY_PARAMS.STATEMENT_MONTH, next)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementMonth])

  useEffect(() => {
    if (transactionType) {
      setParams(QUERY_PARAMS.TRANSACTION_TYPE, transactionType.id)
    } else {
      deleteParam(QUERY_PARAMS.TRANSACTION_TYPE)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionType])

  useEffect(() => {
    if (reconcileStatus) {
      setParams(QUERY_PARAMS.RECONCILIATION_STATUS, reconcileStatus.id)
    } else {
      deleteParam(QUERY_PARAMS.RECONCILIATION_STATUS)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconcileStatus])

  // Field → parent: report the user's edits so the parent (session owner) can persist them.
  useEffect(() => {
    onStatementBalanceChange?.(Number(statementBalance || 0))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementBalance])

  // Parent → field: reflect externally-set values (session hydration, Start Over). The numeric-equality guard
  // stops the two effects from ping-ponging; React's useState also bails when the value is unchanged.
  useEffect(() => {
    if (Number(statementBalance || 0) !== Number(statementBalanceProp || 0)) {
      setValue('statementBalance', statementBalanceProp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementBalanceProp])

  // Reconciliation carry-forward drives the (read-only) Beginning/Cleared balance display only.
  useEffect(() => {
    if (accountReconciledData) {
      const _accountClearedBalance = currentReconciliation?.clearedBalance || lastReconciliation?.clearedBalance
      setValue('beginningBalance', _accountClearedBalance || openingBalance)
      setValue('clearedBalance', _accountClearedBalance || 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountReconciledData])

  return (
    <FormProvider {...methods}>
      <Box width="100%">
        <Box display={'flex'} alignItems={'center'} gap={2} flexWrap={'wrap'}>
          <Box>
            <Box sx={{ marginBottom: '4px', color: '#6E0000' }}>Accounts</Box>
            <AccountsDropdown
              setParams={setParams}
              accountId={accountId}
              isBankAccount={true}
              defaultSelectedAccountSortCode={'1036'}
            />
          </Box>

          <Box>
            <Box sx={{ marginBottom: '4px', color: '#6E0000' }}>Type</Box>
            <Controller
              name="transactionType"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <Autocomplete
                    options={transactionTypeOptions}
                    value={value}
                    onChange={(event, item) => {
                      onChange(item)
                    }}
                    getOptionLabel={option => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="Select Type"
                        sx={{
                          width: '220px',
                          '& .MuiOutlinedInput-root': {
                            height: '56px'
                          }
                        }}
                      />
                    )}
                    sx={{
                      '& .MuiAutocomplete-input': {
                        height: '14px'
                      }
                    }}
                  />
                )
              }}
            />
          </Box>

          <Box>
            <Box sx={{ marginBottom: '4px', color: '#6E0000' }}>Status</Box>
            <Controller
              name="reconcileStatus"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <Autocomplete
                    options={reconcileStatusOptions}
                    value={value}
                    disabled={lineItem?.toString() !== 'true'}
                    onChange={(event, item) => {
                      onChange(item)
                    }}
                    getOptionLabel={option => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="Select Status"
                        sx={{
                          width: '220px',
                          '& .MuiOutlinedInput-root': {
                            height: '56px'
                          }
                        }}
                      />
                    )}
                  />
                )
              }}
            />
          </Box>

          <Box>
            <Box sx={{ marginBottom: '4px', color: '#6E0000' }}>Statement</Box>
            <FormControl variant="outlined">
              <Controller
                name="statementMonth"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error, invalid } }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      views={['year', 'month']}
                      value={value}
                      onChange={value => {
                        onChange(value)
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          inputProps={{ ...params.inputProps, readOnly: true }}
                          error={invalid}
                          helperText={invalid ? error?.message : null}
                        />
                      )}
                      maxDate={dayjs().add(1, 'month').endOf('month')}
                      minDate={dayjs().subtract(25, 'year').startOf('year')}
                    />
                  </LocalizationProvider>
                )}
              />
            </FormControl>
          </Box>

          {showMonthChangePrompt && sessionMonth && (
            <Box sx={{ flex: '1 1 280px', maxWidth: 440, minWidth: 240 }}>
              <MuiAlert
                severity="warning"
                sx={theme => ({
                  py: 0.5,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  '& .MuiAlert-icon': { color: theme.palette.primary.main },
                  '& .MuiAlert-message': { width: '100%' }
                })}
              >
                <Typography fontSize="13px">
                  This session is associated with <b>{dayjs(sessionMonth).format('MMMM YYYY')}</b>
                  {sessionAccountName ? (
                    <>
                      {' '}
                      and bank account <b>{sessionAccountName}</b>
                    </>
                  ) : null}
                  . If you want to change it to <b>{selectedMonthLabel}</b>, click Change.
                  {willTrimTransactions ? (
                    <>
                      {' '}
                      Any selected transactions dated after the end of <b>{selectedMonthLabel}</b> will be removed from
                      this session.
                    </>
                  ) : null}
                </Typography>
                <Box display="flex" gap={1} justifyContent="flex-end" mt={1}>
                  <Button color="inherit" size="small" onClick={handleCancelMonthChange} disabled={monthChangeBusy}>
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    size="small"
                    variant="contained"
                    onClick={onConfirmMonthChange}
                    disabled={monthChangeBusy}
                  >
                    Change
                  </Button>
                </Box>
              </MuiAlert>
            </Box>
          )}
        </Box>

        <Box display="flex" mt={2} gap={2}>
          <Box>
            <Box sx={{ marginBottom: '4px', color: '#6E0000' }}>Beginning Balance</Box>
            <FormControl variant="outlined" sx={{ '& .MuiFormControl-root': { margin: 0 } }}>
              <CustomController
                controllerName="beginningBalance"
                controllerLabel=""
                fieldType="number"
                placeholder="Beginning Balance"
                isDisabled
              />
            </FormControl>
          </Box>

          <Box maxWidth={220} width="100%">
            <Box sx={{ marginBottom: '4px', color: '#6E0000' }}>Statement Balance</Box>
            <FormControl variant="outlined" sx={{ '& .MuiFormControl-root': { margin: 0 } }}>
              <CustomController
                controllerName="statementBalance"
                controllerLabel=""
                fieldType="number"
                placeholder="Statement Balance"
              />
            </FormControl>
          </Box>

          <Box sx={{ marginLeft: 'auto' }}>
            <Box display="flex" justifyContent="flex-end">
              <Typography variant="body1" textAlign="right" mr={4}>
                <b>Cleared Balance:</b>
              </Typography>
              {/* NOT abs(): a cleared balance genuinely can be negative, and hiding the sign turns an overdrawn
                  book balance into a plausible-looking positive one. */}
              <Typography variant="body1">{formatCurrency(Number(clearedBalance || 0))}</Typography>
            </Box>

            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Typography variant="body1" textAlign="right" mr={4}>
                <b>Amount To be Cleared:</b>
              </Typography>
              <Typography variant="body1">
                {formatCurrency(Number(statementBalance || 0) - clearedBalance)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
          <Box>
            <FormControlLabel
              label="Line Item Detail"
              control={
                <Controller
                  name="lineItems"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Checkbox
                      checked={value}
                      onChange={e => {
                        if (!accountId) {
                          Alert.error('Please select an account')
                        } else {
                          onChange(e)
                        }
                      }}
                    />
                  )}
                />
              }
            />
            <Typography
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '5px' }}
              color="text.secondary"
              fontSize="14px"
              mt={1}
            >
              <InfoIcon sx={{ width: '15px', height: '15px' }} color="action" />
              Checks/debits are grouped by day here. Switch on "Line Item Detail" to see individual checks and
              search them.
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" alignItems="flex-end" width="30%">
            <Box display="flex" alignItems="flex-end">
              <Box display="flex" justifyContent="flex-end" border={'solid #e0e0e0'} borderRight={0} p={1}>
                <Typography variant="body1" textAlign="right" mr={4}>
                  <b>Total Credit:</b>
                </Typography>
                <Typography variant="body1">{formatCurrency(Number(creditTotal || 0))}</Typography>
              </Box>

              <Box display="flex" justifyContent="flex-end" border={'solid #e0e0e0'} p={1}>
                <Typography variant="body1" textAlign="right" mr={4}>
                  <b>Total Debit:</b>
                </Typography>
                <Typography variant="body1">{formatCurrency(Number(debitTotal || 0))}</Typography>
              </Box>
            </Box>
            {lineItem?.toString() !== 'true' && (
              <Typography color="text.secondary" fontSize="12px" mt={0.5} textAlign="right">
                Totals cover every transaction selected in this session, including any not shown by the current
                filters.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </FormProvider>
  )
}

export default BankReconciliationFilters
